import { _ } from "libs";

import React, { FunctionComponent, useContext, useMemo, useState } from "react";
import moment from "moment-timezone";
import ReservationPageContext from "./reservationPageContext";
import ToggleSwitch from "../../toggleSwitch";
import Players from "./players";
import CourtItemMedium from "../../items/court/medium";
import { Card, Court, PaymentMethod, Reservation, ReservationContext, Voucher } from "../../../types";
import Loader from "../../loader";
import CourtSelect from "./courtSelect";
import DurationSelect from "./durationSelect";
import { displayCardNetwork, extractDurations } from "../../../actions/helpers";
import { flash } from "../../../actions";
import cx from "classnames";
import { useStripe } from "@stripe/react-stripe-js";

function getCardLast4(cards: Card[], id: string): string {
	for (let card of cards) {
		if (card.cardId === id) {
			return card.last4;
		}
	}
	return "";
}

function getCardNetworks(cards: Card[], id: string): string {
	for (let card of cards) {
		if (card.cardId === id) {
			return card.networks?.available?.[0];
		}
	}
	return "";
}

function getCardBrand(cards: Card[], id: string): string {
	for (let card of cards) {
		if (card.cardId === id) {
			return card.brand;
		}
	}
	return "";
}

function getVoucherCode(context: ReservationContext): string {
	let voucherCode = "";
	if (context) {
		const voucher = context.vouchers[0] as Voucher;
		if (voucher) {
			if (voucher.valid && voucher.available) {
				voucherCode = voucher.voucherCode;
			}
		}
	}
	return voucherCode;
}

const BookingError: FunctionComponent<{ error: string }> = ({ error }: { error: string }) => {
	if (!error) {
		return null;
	}

	return <div className="msg error mb-4">{error}</div>;
};

const BookingSummary: FunctionComponent<{}> = () => {
	const pageContext = useContext(ReservationPageContext);
	const stripe = useStripe();
	const {
		profile,
		reservation,
		clubInfo,
		courts,
		setPage,
		validateAndBook,
		getReservationContext,
		clearReservationContext,
		removePartnerFromReservation,
		editing,
		onCloseClicked,
		onBookingSuccess,
	} = pageContext;
	const { context, params, court, cards, selectedCard, selectedNetwork, networks } = reservation;
	const { duration, startDateTime, ballMachine, paymentMethod } = params;

	const timezone = clubInfo.timeZone;

	const [submitting, setSubmitting] = useState(false);

	const hasBallMachine = useMemo(() => {
		if (!court) {
			return false;
		}
		return court.ballMachine;
	}, [court]);

	const onCourtChange = (court: Court) => {
		getReservationContext({ court });
	};

	const onDurationChange = (duration: number) => {
		getReservationContext({ duration });
	};

	const onPlayerRemove = (index: number) => {
		removePartnerFromReservation(index);
		getReservationContext({});
	};

	const onPaymentCellClick = () => {
		if (context && !editing) {
			if (paymentMethod === PaymentMethod.CreditCard && !selectedCard) {
				setPage("addCard");
			} else {
				setPage("payment");
			}
		}
	};

	const onNetworkCellClick = () => {
		if (context) {
			setPage("paymentNetwork");
		}
	};

	const onSaveClick = () => {
		setSubmitting(true);
		validateAndBook(
			stripe,
			({ data: { reservation } }: { data: { reservation: Reservation } }, edit: boolean) => {
				setSubmitting(false);
				if (edit) {
					flash(_("editReservation.savedConfirmationMessage"));
					onCloseClicked(reservation);
				} else {
					setPage("confirmation");
					onBookingSuccess();
				}
			},
			() => {
				setSubmitting(false);
			}
		);
	};

	// Select championship by default
	// in case there is no selected and
	// at least one championship is available

	if (reservation?.params?.championshipId === null || reservation?.params?.championshipId === undefined) {
		if (reservation?.context?.championships.length > 0) {
			let defaultChampionship = reservation?.context?.championships.find(
				(e) => e.available === true && e.settings.preselectDefault === true
			);
			reservation.params.championshipId = defaultChampionship?.uuid;
		} else {
			reservation.params.championshipId = null;
		}
	}

	let championshipId = reservation?.params?.championshipId ? reservation.params.championshipId : false;
	let championship = reservation?.context?.championships.find((e) => e.uuid === championshipId);
	let championshipName = championship?.name;

	return (
		<div className="createReservation wizard">
			<div className="wizardHeader">
				<div
					className="close"
					onClick={() => (onCloseClicked ? onCloseClicked() : clearReservationContext())}
				/>
				<div className="title">
					{editing ? _("bookingWizard.editing.summary.heading") : _("bookingWizard.summary.heading")}
				</div>
			</div>
			<div className="wizardBody">
				<BookingError error={reservation.error} />
				<div className="bookingDate">{moment.unix(startDateTime).tz(timezone).format("dddd, MMMM D")}</div>
				<div className="bookingTime">
					{moment.unix(startDateTime).tz(timezone).format("H:mm")}
					{editing ? null : ", " + _("reservation.duration", duration, { duration })}
				</div>

				{editing ? (
					<>
						{!reservation.context ? (
							<Loader />
						) : (
							<>
								<div className="group">
									<h6>{_("reservationDetail.court.heading")}</h6>
									<CourtSelect
										courts={courts}
										value={reservation.params.courtId}
										onSelect={onCourtChange}
										clubId={clubInfo.clubId}
										timestamp={reservation.selectedReservation.startDateTime}
										loadingText={_("loader.defaultText")}
									/>
								</div>
								<div className="group">
									<h6>{_("bookingWizard.duration.heading")}</h6>
									<DurationSelect
										durations={extractDurations(context.rules)}
										value={reservation.params.duration}
										onSelect={onDurationChange}
									/>
								</div>
							</>
						)}
					</>
				) : null}

				{!editing && court ? <CourtItemMedium court={court} extraClass="capsule" /> : null}

				<div className="table">
					<div className="cell playersCell">
						<div className="tableRow">
							<div className="tableRowIcon" />
							<div className="tableRowLabel">{_("bookingWizard.playersCell.label")}</div>
							<div className="tableRowValue" />
						</div>
					</div>

					{!reservation.context ? (
						<Loader />
					) : (
						<Players
							onlyMe={false}
							me={profile}
							partners={reservation.partners}
							duration={reservation.params.duration}
							rules={context.rules}
							onAddClick={(e) => {
								if (context) {
									setPage("partner");
								}
							}}
							onRemoveClick={onPlayerRemove}
						/>
					)}
					{hasBallMachine && (
						<div className="cell ballMachineCell">
							<div className="tableRow">
								<div className="tableRowIcon" />
								<div className="tableRowLabel">{_("bookingWizard.ballMachineCell.label")}</div>
								<div className="tableRowValue">
									<ToggleSwitch
										enabled={ballMachine}
										onChange={(ballMachine) => getReservationContext({ ballMachine })}
									/>
								</div>
							</div>
						</div>
					)}
					{!ballMachine && (
						<div
							className={cx("cell", "championshipCell", { disclosureIndicator: !editing })}
							onClick={(e) => {
								if (context && !editing) {
									setPage("championship");
								}
							}}
						>
							<div className="tableRow">
								<div className="tableRowIcon" />
								<div className="tableRowLabel">{_("bookingWizard.championshipCell.label")}</div>
								<div className="tableRowValue">{championshipName}</div>
							</div>
						</div>
					)}
					<div
						className={cx("cell paymentCell", { disclosureIndicator: !editing })}
						onClick={onPaymentCellClick}
					>
						<div className="tableRow">
							<div className="tableRowIcon" />
							<div className="tableRowLabel">{_("bookingWizard.paymentCell.label")}</div>
							<div className="tableRowValue horizontal">
								{paymentMethod === PaymentMethod.CreditCard ? (
									selectedCard ? (
										[
											<div
												className={`creditCardIcon ${getCardBrand(
													cards,
													selectedCard
												).toLowerCase()}`}
												key="brand"
											/>,
											<div className="creditCardEnding" key="ending">
												...{getCardLast4(cards, selectedCard)}
											</div>,
										]
									) : (
										<div className="addCard">
											<a>{_("bookingWizard.payment.addCardLink")}</a>
										</div>
									)
								) : paymentMethod ? (
									_(`bookingWizard.payment.${paymentMethod}.label`)
								) : (
									<Loader />
								)}
							</div>
						</div>
					</div>
					{paymentMethod === PaymentMethod.CreditCard && selectedCard && networks?.length > 1 && (
						<div
							className={cx("cell paymentCell", { disclosureIndicator: true })}
							onClick={onNetworkCellClick}
						>
							<div className="tableRow">
								<div className="tableRowIcon" />
								<div className="tableRowLabel">{_("bookingWizard.cardNetworks.label")}</div>
								<div className="tableRowValue">{displayCardNetwork(selectedNetwork)}</div>
							</div>
						</div>
					)}
					<div
						className="cell voucherCell disclosureIndicator"
						onClick={(e) => {
							if (context) {
								setPage("voucher");
							}
						}}
					>
						<div className="tableRow">
							<div className="tableRowIcon" />
							<div className="tableRowLabel">{_("bookingWizard.voucherCell.label")}</div>
							<div className="tableRowValue">{getVoucherCode(context)}</div>
						</div>
					</div>
					<div className="cell priceCell">
						<div className="tableRow">
							<div className="tableRowIcon" />
							<div className="tableRowLabel">{_("bookingWizard.priceCell.label")}</div>
							<div className="tableRowValue">
								{context ? (
									<span>
										{context.totalCost.toFixed(2)} {context.currency}
									</span>
								) : null}
							</div>
						</div>
					</div>
					<div className="note small">
						{context ? (
							<div>
								{context.canCancel ? (
									<span>
										{_("reservation.canBeCancelledUntil", null, {
											until: moment(context.canCancelUntil * 1000)
												.tz(timezone)
												.format("ddd, D MMMM H:mm"),
										})}
									</span>
								) : (
									<span>{_("reservation.cannotBeCancelled")}</span>
								)}
							</div>
						) : null}
					</div>
				</div>
			</div>
			<div className="wizardFooter">
				<button
					disabled={submitting}
					className={`button large submit fullWidth ${submitting ? "submitting" : ""}`}
					onClick={onSaveClick}
				>
					{submitting
						? _("loader.defaultText")
						: editing
						? _("bookingWizard.editing.saveBtn")
						: _("bookingWizard.btnBook.label")}
				</button>
			</div>
		</div>
	);
};

export default BookingSummary;
